import { LDClient, LDContext } from "launchdarkly-react-client-sdk";
import { INITIAL_CONTEXT } from "~/integrations/launch-darkly";
import { logWarning } from "~/shared/services/error-reporting";

// The SDK sets no timeout on its flag request. 3s leaves the next identify time to land before FeatureFlagProvider stops waiting at 5s.
const IDENTIFY_TIMEOUT_MS = 3000;

const isSame = (a: LDContext | undefined, b: LDContext | undefined) =>
  JSON.stringify(a) === JSON.stringify(b);

// The SDK applies whichever identify response lands last, with no staleness check, so overlapping calls let an older context win.
export const createLatestContextIdentifier = (
  client: Pick<LDClient, "identify">,
  onStaleFlagsChange: (holdsStaleFlags: boolean) => void,
) => {
  let latestContext: LDContext = INITIAL_CONTEXT;
  // Undefined makes the next update identify again, even with an equal context.
  let requestedContext: LDContext | undefined = INITIAL_CONTEXT;
  let pendingRequest: Promise<unknown> | undefined;
  let holdsStaleFlags = false;
  let isActive = false;

  const setHoldsStaleFlags = (value: boolean) => {
    if (holdsStaleFlags !== value) {
      holdsStaleFlags = value;
      onStaleFlagsChange(value);
    }
  };

  const identifyLatestContext = () => {
    if (
      !isActive ||
      pendingRequest ||
      isSame(requestedContext, latestContext)
    ) {
      return;
    }
    const context = latestContext;
    requestedContext = context;
    const request = client.identify(context);
    pendingRequest = request;

    const timeout = setTimeout(() => {
      logWarning("LaunchDarkly identify timed out");
      pendingRequest = undefined;
      identifyLatestContext();
    }, IDENTIFY_TIMEOUT_MS);

    request.then(
      () => {
        clearTimeout(timeout);
        onIdentified(context, request);
      },
      (error: unknown) => {
        clearTimeout(timeout);
        onFailed(context, request, error);
      },
    );
  };

  const onIdentified = (context: LDContext, request: Promise<unknown>) => {
    const answeredLate = pendingRequest !== request;
    if (!answeredLate) {
      pendingRequest = undefined;
    }

    if (isSame(context, latestContext)) {
      setHoldsStaleFlags(false);
    } else if (answeredLate) {
      setHoldsStaleFlags(true);
      // A pending request lands after this answer and corrects the flags itself.
      if (!pendingRequest) {
        requestedContext = undefined;
      }
    }
    identifyLatestContext();
  };

  const onFailed = (
    context: LDContext,
    request: Promise<unknown>,
    error: unknown,
  ) => {
    logWarning(
      `LaunchDarkly identify failed: ${error instanceof Error ? error.message : String(error)}`,
    );
    if (pendingRequest === request) {
      pendingRequest = undefined;
    }

    // Retrying now would loop while offline, so the next context update retries.
    if (isSame(context, latestContext)) {
      if (!pendingRequest) {
        requestedContext = undefined;
      }
      return;
    }
    identifyLatestContext();
  };

  return {
    update: (context: LDContext) => {
      latestContext = context;
      identifyLatestContext();
    },
    start: () => {
      isActive = true;
      identifyLatestContext();
    },
    stop: () => {
      isActive = false;
    },
  };
};
